import { PlusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Spin, Statistic } from "antd";
import { useNavigate } from "react-router-dom";
import { useEndTime, useStartTime } from "../DateContext/DateContext";
import { useAutomations } from "./useAutomations";
import "./AutomationsCard.css";
import moment from "moment";
import { BotExecutionsRpc } from "../../lib/discordTypes";
import useDiscordBotTemplates from "../DiscordTriggers/useDiscordBotTemplates";

// render this component if there are executions
const Executions = ({ executions }: { executions: BotExecutionsRpc[] }) => {
  const navigate = useNavigate();

  return (
    <>
      {executions.map((exec) => (
        <p className="m-0 mb-1 text-sm" key={exec.id}>
          {exec.username} @ {moment(exec.updated_at).format("hh:mma")} -{" "}
          {exec.trigger_name}
        </p>
      ))}
      <p
        onClick={() => {
          navigate("/automation");
        }}
        className="mt-5 text-center text-dots-vibrant-orange cursor-pointer mb-0"
      >
        Track all
      </p>
    </>
  );
};
// render this component if there are no executions in this time period
const ZeroExecutions = () => {
  const navigate = useNavigate();
  return (
    <>
      <div className="px-4 py-3 bg-red-100 rounded">
        <p>
          😔 No executions for your bots this period. Let your members know
          about the bot!
        </p>
        <p className="m-0">Or you can click here to edit an existing one.</p>
      </div>
      <p
        onClick={() => {
          navigate("/automation");
        }}
        className="mt-5 mb-0 text-center text-dots-vibrant-orange cursor-pointer"
      >
        Edit / Track
      </p>
    </>
  );
};

// render this component if there are no live triggers on this community
const ZeroAutomations = () => {
  const { botTemplates } = useDiscordBotTemplates();
  const navigate = useNavigate();
  const triggerButtonStyle =
    "flex items-center rounded-md px-1 py-1 w-48 h-16 cursor-pointer overflow-hidden relative";
  return (
    <>
      <div className="px-4 py-3 bg-red-100 rounded">
        <p className="m-0">
          You have no live bots yet! Click below to add one.
        </p>
      </div>
      <div className="automations-card-grid mt-4">
        <Button
          onClick={() => {
            window.analytics.track(
              "Widget - new automation from scratch clicked"
            );
            navigate("/automation/new?type=scratch");
          }}
          className="flex"
          style={{ minWidth: "230px" }}
        >
          <PlusCircleOutlined className="text-lg" />
          Start from scratch
        </Button>
        {botTemplates.slice(0, 5).map((templ) => (
          <Button
            onClick={() => {
              window.analytics.track(
                "Widget - new automation from scratch clicked"
              );
              navigate(`/automation/new?type=${templ.slug}`);
            }}
            className="flex"
            style={{ minWidth: "230px" }}
          >
            {templ.emoji}
            {templ.name}
          </Button>
        ))}
      </div>
      <p
        onClick={() => {
          navigate("/automation/new?type=templates");
        }}
        className="mt-5 mb-0 text-center text-dots-vibrant-orange cursor-pointer"
      >
        Explore more bot templates
      </p>
    </>
  );
};

export default function AutomationsCard() {
  const { startTime } = useStartTime();
  const { endTime } = useEndTime();
  const navigate = useNavigate();

  const { executionsCount, loading, error, automationsCount, executions } =
    useAutomations(startTime, endTime);

  return (
    <Spin spinning={loading}>
      <Card title="Bots" bordered={false}>
        {error ? (
          <div className="px-4 py-3 bg-red-100 rounded">
            <p className="m-0">An error occured, please contact support</p>
          </div>
        ) : (
          <div className="flex flex-col w-full h-4/5">
            {automationsCount > 0 ? (
              <>
                <div className="flex justify-between">
                  <Statistic title="Bot executions" value={executionsCount} />
                  <div
                    onClick={() => {
                      navigate("/automation/new?type=templates");
                    }}
                    className="flex border-2 border-gray-200 rounded-md w-36 items-center justify-center font-medium text-xl cursor-pointer hover:bg-gray-50"
                  >
                    <PlusOutlined className="mx-4" />
                    <p className="text-sm font-normal m-0">Add a new bot</p>
                  </div>
                </div>
                <div className="mt-5">
                  <p className="font-bold mb-1">Recent bot executions</p>
                  {executions && executions.length > 0 ? (
                    <Executions executions={executions} />
                  ) : (
                    <ZeroExecutions />
                  )}
                </div>
              </>
            ) : (
              <ZeroAutomations />
            )}
          </div>
        )}
      </Card>
    </Spin>
  );
}
